import { useMutation, useQuery } from '@apollo/client'
import { Button, Link, Modal, Text } from '@chaine/keychaine'
import React, { useState } from 'react'

import { Toast } from '../../../_shared/components/toast'
import { APIKeyColumn } from '../../developer/api-key-column'
import { AppModalProps } from '../components/apps-data'
import { CREATE_APP_API_KEY } from '../data/mutations'
import { AppAPIKeyReturnType, GET_APP_API_KEY } from '../data/queries'

/**
 * Generate's an api key for app's that require it..
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const CreateAppAPIKeyModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, app, modalTitle, modalSubtitle } = props
  const [apiKey, setAPIKey] = useState<string | null>(null)

  const { showToast } = Toast()

  const { data, refetch } = useQuery<AppAPIKeyReturnType>(GET_APP_API_KEY, {
    fetchPolicy: 'network-only',
    onCompleted: (result: AppAPIKeyReturnType | null) => {
      if (result && result.appAPIKey) {
        setAPIKey(result.appAPIKey.apiKey)
      }
    },
    variables: { appType: appType }
  })

  const chaineAppAPIToken = data?.appAPIKey?.apiKey

  const [createAppAPIKey, { loading: isCreatingAPIKey }] = useMutation(CREATE_APP_API_KEY, {
    onCompleted: (result) => {
      setAPIKey(result.createAppAPIKey.value)
      refetch()
    },
    onError: (error) => {
      showToast({ status: 'error', title: error.message })
    }
  })

  const handleCreateAPIKeySubmit = () => {
    createAppAPIKey({
      variables: { appType: appType, source: appType }
    })
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          !apiKey ? modalTitle || `Enable ${app.appName} for Chaine` : `${app.appName} is enabled for your workspace`
        }
        subtitle={
          !apiKey
            ? modalSubtitle || `You must generate an API key and provide it to ${app.appName} support.`
            : `An API Key is generated for this app. Click 'Reveal key' to view it.`
        }
        footer={
          <>
            {!apiKey ? (
              <Button isLoading={isCreatingAPIKey} variant={'primary'} mr={3} onClick={handleCreateAPIKeySubmit}>
                Create Key
              </Button>
            ) : (
              <></>
            )}
            <Button onClick={onClose}>{!apiKey ? 'Cancel' : 'Close'}</Button>
          </>
        }
      >
        <>
          {apiKey ? (
            <>
              <APIKeyColumn apiKey={{ apiKey: chaineAppAPIToken || '', created: '', modified: '' }}></APIKeyColumn>
            </>
          ) : (
            <Text>
              <Link color="blue.500" href={app.docs}>
                Read these instructions
              </Link>
              {` to enable the ${app.appName} app`}
            </Text>
          )}
        </>
      </Modal>
    </>
  )
})

CreateAppAPIKeyModal.displayName = 'CreateAppAPIKey'

export { CreateAppAPIKeyModal }
